import React, { useContext, useEffect, useState } from 'react';
import { Box, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { ArrowBackIosNew, ArrowForwardIosRounded } from '@mui/icons-material';
import Styles from './InstructionsPane.module.scss'
import { toast } from 'react-toastify';
import { copyEnabledChecker } from 'utils/helpers/utility';
import { AuthContext } from 'components/lib';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { CodeBlock } from 'components/CodeBlock/CodeBlock';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';

SyntaxHighlighter.registerLanguage('jsonata', () => ({
  case_insensitive: false,
  keywords: {
    keyword: 'true false null',
    built_in: '$sum $count $distinct $sort $filter $map $reduce $average $min $max $string $number $boolean $exists $type $keys $lookup $spread $merge $each $sift $single $append $join $split $substring $length $trim $uppercase $lowercase $pad $contains $replace $match $eval $abs $floor $ceil $round $power $sqrt $random $millis $now $fromMillis $toMillis $formatNumber $formatBase $parseInteger $encodeUrl $encodeUrlComponent $decodeUrl $decodeUrlComponent $base64encode $base64decode $assert $error'
  },
  contains: [
    {
      className: 'built_in',
      begin: /\$(?:sum|count|distinct|sort|filter|map|reduce|average|min|max|string|number|boolean|exists|type|keys|lookup|spread|merge|each|sift|single|append|join|split|substring|length|trim|uppercase|lowercase|pad|contains|replace|match|eval|abs|floor|ceil|round|power|sqrt|random|millis|now|fromMillis|toMillis|formatNumber|formatBase|parseInteger|encodeUrl|encodeUrlComponent|decodeUrl|decodeUrlComponent|base64encode|base64decode|assert|error)\b/,
      relevance: 15
    },

    {
      className: 'variable',
      begin: /\$[a-zA-Z_][a-zA-Z0-9_]*/,
      relevance: 10
    },

    {
      className: 'keyword',
      begin: /\$\$/,
      relevance: 12
    },

    {
      className: 'keyword',
      begin: /\$(?![a-zA-Z_])/,
      relevance: 12
    },

    {
      className: 'comment',
      begin: /\/\*/,
      end: /\*\//,
      relevance: 0
    },
    {
      className: 'comment',
      begin: /\/\//,
      end: /$/,
      relevance: 0
    },

    {
      className: 'string',
      begin: /"/,
      end: /"/,
      contains: [{ begin: /\\./ }],
      relevance: 0
    },

    {
      className: 'string',
      begin: /'/,
      end: /'/,
      contains: [{ begin: /\\./ }],
      relevance: 0
    },

    {
      className: 'number',
      begin: /\b\d+(\.\d+)?([eE][+-]?\d+)?\b/,
      relevance: 0
    },

    {
      className: 'literal',
      begin: /\b(true|false|null)\b/,
      relevance: 0
    },

    {
      className: 'operator',
      begin: /[+\-*\/=<>!&|?:~]/,
      relevance: 0
    },

    {
      className: 'punctuation',
      begin: /[{}\[\](),.;]/,
      relevance: 0
    }
  ]
}));
export function InstructionsPane({
  steps = [],
  isMuted,
  currentStep = 0,
  handleVoiceClick,
  nodeInfo,
  open: openProp,
  onToggle,
  t
}) {
  const context = useContext(AuthContext);
  const [copied, setCopied] = useState(false);
  const isCopyEnabled = copyEnabledChecker(context?.user)
  useEffect(() => {
  }, [openProp]);

  const toggle = () => {
    if (onToggle) onToggle(!openProp);
  };
  const isAssessment = nodeInfo.is_assessment === 1;
  const expInput = steps[currentStep]?.exp_input;
  const hasExpInput = expInput && expInput.length > 0;
  const codeValue = isAssessment
  ? steps[currentStep].command
  : hasExpInput
    ? expInput
    : null;

  const handleCopyButton = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
      toast.dark(t('Labs.Nodes.NodeJSONata.toast.warn'), {
        type: 'warning',
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch (error) {
      toast.dark(t('Labs.Nodes.NodeJSONata.copyCode.alert'), {
        type: 'error',
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }
  const handleCopy = async (e) => {
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
    toast.dark(t('Labs.Nodes.NodeJSONata.toast.warn'), {
      type: 'warning',
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const handleCopyDisabled = (e) => {
    e.preventDefault();
  }


  const step = steps?.[currentStep] || {};

  return (
    <Paper
      id="instructionsContainer"
      elevation={4}
      sx={{
        right: 8,
        height: '100%',
        transition: 'width 200ms ease',
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
        zIndex: 1,
        borderRadius: 1,
      }}
      aria-expanded={openProp}
      role="complementary"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          backgroundColor: 'slategrey',
          color: 'white',
          width: openProp ? '10%' : '75%'
        }}
      >
        {openProp ? (
          <IconButton
            onClick={toggle}
            aria-label='Collapse instructions'
            size="medium"
            sx={{ color: 'inherit' }}
          >
            <ArrowForwardIosRounded fontSize='inherit' sx={{ color: 'white' }} />
          </IconButton>
        ) : (<>
          <IconButton
            onClick={toggle}
            aria-label='Open instructions'
            size="small"
            sx={{ color: 'inherit' }}
          >
            <ArrowBackIosNew fontSize='inherit' />
          </IconButton>
          <Typography
            sx={{
              writingMode: 'vertical-rl',
              color: 'white',
            }}
          >
            {t('Labs.Nodes.NodeJSONata.panel.button.instructionPanel')}
          </Typography>
        </>)}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: openProp ? 'flex' : 'none',
          flexDirection: 'column',
          overflow: 'hidden',
        }}

      >
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: 'row' }}>
          <Typography variant="subtitle1" noWrap>
            {step.title || 'Instructions'}
          </Typography>
          <div>
            <Tooltip title="Toggle Sound">
              <span>
                <IconButton size="small" onClick={handleVoiceClick}>
                  {isMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </Box>

        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          <div
            id='instruction'
            className={Styles.instruction}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(marked.parse(steps[currentStep]?.instructions || ''))
            }}
          />
          {codeValue && (
            <div className={Styles.codeBlockContainer}>
              <CodeBlock
                t={t}
                language="jsonata"
                value={codeValue}
                copied={copied}
                isCopyEnabled={isCopyEnabled}
                handleCopy={handleCopy}
                handleCopyDisabled={handleCopyDisabled}
                handleCopyButton={()=>handleCopyButton(
                isAssessment
                  ? steps[currentStep]?.command
                  : steps[currentStep]?.exp_input)}
                theme='light'
              />
            </div>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
